import moment from 'moment';
import Terminart from '@/types/Terminart';
import {Vue} from 'vue-property-decorator';

export class Termin {
    public termin_id: number | null = null;
    public datum: string | null = null;
    public zeit: string | null = null;
    public platznr: number | null = null;
    public dauer: string | null = null;
    public anmeldung_id: number | null = null;
    public patnr: number | null = null;
    public anmeldung_pat_patnr: number | any = null;
    public nachname: string | null = null;
    public vorname: string | null = null;
    public hinweis: string | null = null;
    public terminart: number | null = null;
    public erschienen: string | null = 'n';
    public terminserie_id: number | null = null;
    public serienmuster: string | null = null;
    public beschreibung: string | null = null;
    public seriendauer_von: string | null = null;
    public seriendauer_bis: string | null = null;
    public pat_vorname: string | null = null;
    public pat_nachname: string | null = null;
    public pat_geburtsdatum: string | null = null;
    public pat_strasse: string | null = null;

    public fw_created: string | null = null;
    public anforderung_bezeichnung = '';
    public anforderung_dauer: string | null = '';
    public svnr: string | null = null;
    public email: string | null = null;
    public telefon: string | null = null;
    public status: string | null = null;

    public get anforderung(): string {
        if (this.hinweis) return this.anforderung_bezeichnung + ' ' + this.hinweis;
        else return this.anforderung_bezeichnung;
    }

    public get statusBezeichnung():string {
       if (this.status === 'A') return 'Angelegt';
       else if (this.status === 'S') return 'Storniert';
       else if (this.status === 'V') return 'Verarbeitet';
       else return '';
    }

    public get createdDisplay(): string {
        return Vue.prototype.$dt(this.fw_created);
    }

    public get termin(): string {
        this.dauer = this.anforderung_dauer;
        return Vue.prototype.$d(this.datum) + ' / ' + this.zeit;
    }

    public get patientName(): string {
        return this.patnr
            ? [this.pat_vorname, this.pat_nachname].filter((x) => x).join(' ')
            : this.vorname ?? '';
    }

    public get isAnmeldung(): boolean {
        return !!this.anmeldung_id;
    }

    constructor(data?: Partial<Termin>) {
        Object.assign(this, data);
    }
}

export class Termindauer {
    public dauer: string | null = null;

    constructor(data?: Partial<Termindauer>) {
        Object.assign(this, data);
    }
}

export class Terminplatz {
    public platznr: number | null = null;
    public beschriftung: string | null = null;

    constructor(data?: Partial<Terminplatz>) {
        Object.assign(this, data);
    }
}

export class Terminzeit {
    public zeit: string | null = null;

    constructor(data?: Partial<Terminzeit>) {
        Object.assign(this, data);
    }
}

export interface TerminEvent {
    bezeichnung?: string | null | undefined;
    patient: string | null;
    zeitStart?: string;
    zeitEnde?: string;
    color: string;
    start: number;
    end: number;
    category: string | undefined;
    timed: boolean;
    termin_id?: number | null;
    text?: string | null;
    patnr?: number | null;
    anmeldung_id?: number | null;
    anmeldung_pat_patnr?: number | null;
    erschienen?: string | null;
    textcolor?: string;
    isFreierTermin?: boolean;
}

export class Terminkalender {
    public termine: Termin[] = [];
    public arten: Terminart[] = [];
    public dauer: Termindauer[] = [];
    public plaetze: Terminplatz[] = [];
    public zeiten: Terminzeit[] = [];

    public get defaultPlatz(): Terminplatz {
        return this.plaetze[0] ?? { platznr: -1, beschriftung: '-' };
    }

    public platz(platznr: string): string {
        return this.plaetze.find((platz) => platz.platznr?.toString() === platznr)?.beschriftung ?? '';
    }

    public get events(): TerminEvent[] {
        return this.termine.map((termin: Termin) => {
            const { bezeichnung, color, textcolor } = this.arten
                .find((art: Terminart) => art.terminart === termin.terminart) ?? {};

            const [zeitStunden, zeitMinuten] = termin.zeit?.split(':') ?? [];
            const [dauerStunden, dauerMinuten] = termin.dauer?.split(':') ?? [];

            const zeitStart = moment(termin.datum)
                .set('hour', parseInt(zeitStunden, 10))
                .set('minute', parseInt(zeitMinuten, 10));
            const zeitEnde = zeitStart.clone()
                .add(dauerStunden, 'hours')
                .add(dauerMinuten, 'minutes');

            const patientName = (termin: Termin): string => {
                return (termin.pat_nachname ?? '') + ' ' + (termin.pat_vorname ?? '');
            };

            const patient = termin.patnr ? patientName(termin) : termin.vorname;
            const isErschienen = termin.erschienen === 'j';

            return {
                bezeichnung,
                patient,
                zeitStart: zeitStart.format('HH:mm'),
                zeitEnde: zeitEnde.format('HH:mm'),
                color: isErschienen ? Terminart.ErschienenColor : color ?? Terminart.DefaultColor,
                start: zeitStart.unix() * 1000,
                end: zeitEnde.unix() * 1000,
                category: termin.platznr?.toString() ?? this.defaultPlatz.platznr?.toString(),
                timed: true,
                termin_id: termin.termin_id,
                text: termin.hinweis,
                patnr: termin.patnr,
                anmeldung_id: termin.anmeldung_id,
                anmeldung_pat_patnr: termin.anmeldung_pat_patnr,
                erschienen: termin.erschienen,
                textcolor: isErschienen ? Terminart.ErschienenFontColor : textcolor ?? Terminart.DefaultFontColor,
            };
        });
    }
}
